import { AlertCircle, AlertTriangle, ChevronRight, History, Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader } from '~/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import { cn } from '~/utils/cn';

import type { FunnelIssue, FunnelProductsQuery } from '../../types/batch-funnel.api.types';
import type { StageError } from '../../types/stage-errors.api.types';
import { FUNNEL_TONE_CHIP, severityTone } from '../../utils/batch-funnel';
import { KNOWN_ERROR_STAGES, stageErrorForIssue } from '../../utils/stage-errors';

import { StageErrorDetails, StageErrorMeta } from './stage-error-details';

const SEVERITY_ICON = { error: AlertCircle, warning: AlertTriangle } as const;

/** Stage error that sends the run back to approval — the existing Approve is the retry (§5.4). */
const DELIVERY_NOT_STARTED = 'DELIVERY_NOT_STARTED';

/** Validation trigger failure — recorded on the insights stage, but it is the run's validation. */
const ENRICHMENT_VALIDATION_FAILED = 'ENRICHMENT_VALIDATION_FAILED';

interface FunnelIssuesProps {
  issues: FunnelIssue[];
  onDrill: (title: string, query: FunnelProductsQuery) => void;
  /** Current-attempt `/errors` entries — add ×N and timing to stage-error issues. */
  stageErrors?: StageError[];
  /** ERRORs from earlier run attempts (§5.5), listed collapsed under the issues. */
  earlierAttempts?: StageError[];
}

/**
 * "What should I fix first?" — issue cards in the server's ranked order
 * (severity, then affected desc). Each carries its "What to do" step and, when
 * a per-product list exists (`drillDown`), a View products button. Stage-error
 * issues also get ×N, last-seen time and a root-cause Details expander; ERRORs
 * from earlier run attempts follow, collapsed.
 */
export function FunnelIssues({
  issues,
  onDrill,
  stageErrors = [],
  earlierAttempts = [],
}: FunnelIssuesProps) {
  const { t } = useTranslation('catalog-operations');
  if (issues.length === 0 && earlierAttempts.length === 0) return null;
  return (
    <Card data-testid="batch-funnel-issues-card">
      <CardHeader className="p-6 pb-3">
        <h2 className="text-xl" data-testid="batch-funnel-issues-title">
          {t('batchFunnel.issues.title')}
        </h2>
        <p className="text-muted-foreground text-sm font-light">
          {issues.length > 0
            ? t('batchFunnel.issues.subtitle', { count: issues.length })
            : t('stageErrors.noCurrentIssues')}
        </p>
      </CardHeader>
      <CardContent className="space-y-3 px-6 pb-6" data-testid="batch-funnel-issues-list">
        {issues.map((issue, index) => (
          <IssueCard
            key={issue.id ?? index}
            issue={issue}
            stageError={stageErrorForIssue(issue, stageErrors)}
            onDrill={onDrill}
          />
        ))}
        {earlierAttempts.length > 0 && <EarlierAttempts errors={earlierAttempts} />}
      </CardContent>
    </Card>
  );
}

/** One ranked issue; stage-error issues add meta, the approval hint and Details. */
function IssueCard({
  issue,
  stageError,
  onDrill,
}: {
  issue: FunnelIssue;
  stageError: StageError | null;
  onDrill: FunnelIssuesProps['onDrill'];
}) {
  const { t } = useTranslation('catalog-operations');
  const tone = severityTone(issue.severity);
  const Icon = tone === 'error' || tone === 'warning' ? SEVERITY_ICON[tone] : Info;
  const drillDown = issue.drillDown;
  const isStageError = stageError !== null || issue.rootCause != null;
  const issueKey = issue.id ?? issue.code ?? 'unknown';
  return (
    <article
      className="bg-background flex flex-wrap items-start gap-3 rounded-xl border p-4"
      data-testid={`batch-funnel-issue-item-${issueKey}`}
    >
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
          FUNNEL_TONE_CHIP[tone],
        )}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-foreground flex flex-wrap items-baseline gap-x-2 text-sm font-semibold">
          <span data-testid={`batch-funnel-issue-title-${issueKey}`}>
            {issue.title ?? issue.code}
          </span>
          {isStageError && (
            <StageErrorMeta
              affectedCount={issue.affected}
              occurrences={stageError?.occurrences}
              lastOccurredAt={stageError?.lastOccurredAt}
            />
          )}
        </p>
        {issue.message && (
          <p className="text-muted-foreground text-sm break-words">{issue.message}</p>
        )}
        {issue.nextStep && (
          <p className="text-sm">
            <span className="text-foreground font-medium">{t('batchFunnel.whatToDo')}</span>{' '}
            <span className="text-muted-foreground">{issue.nextStep}</span>
          </p>
        )}
        {issue.code === DELIVERY_NOT_STARTED && (
          <p className="text-muted-foreground text-xs">{t('stageErrors.deliveryNotStartedHint')}</p>
        )}
        {issue.code === ENRICHMENT_VALIDATION_FAILED && (
          <p className="text-muted-foreground text-xs">{t('stageErrors.validationFailedHint')}</p>
        )}
        {issue.retryable === true && (
          <p className="text-muted-foreground text-xs">{t('batchFunnel.issues.retryable')}</p>
        )}
        <StageErrorDetails
          rootCause={issue.rootCause ?? stageError?.rootCause}
          lastOccurredAt={stageError?.lastOccurredAt}
          runId={stageError?.runId}
          testId={`batch-funnel-issue-details-${issueKey}`}
        />
      </div>
      {drillDown && (
        <Button
          size="sm"
          variant="outline"
          className="cursor-pointer"
          data-testid={`batch-funnel-issue-view-products-button-${issueKey}`}
          onClick={() => {
            onDrill(issue.title ?? issue.code ?? '', {
              stage: drillDown.stage,
              outcome: drillDown.outcome,
              reason: drillDown.reason,
            });
          }}
        >
          {t('batchFunnel.viewProducts')}
        </Button>
      )}
    </article>
  );
}

/**
 * ERRORs from earlier run attempts (§5.5) — the run was retried and moved on,
 * so they're history, not a current failure. Collapsed by default.
 */
function EarlierAttempts({ errors }: { errors: StageError[] }) {
  const { t } = useTranslation('catalog-operations');
  const [open, setOpen] = useState(false);
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="border-t pt-3"
      data-testid="batch-funnel-earlier-attempts"
    >
      <CollapsibleTrigger asChild>
        <Button
          data-testid="batch-funnel-earlier-attempts-toggle"
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:text-foreground cursor-pointer gap-1.5 px-1"
        >
          <ChevronRight
            className={cn('h-4 w-4 transition-transform', open && 'rotate-90')}
            aria-hidden="true"
          />
          <History className="h-4 w-4" aria-hidden="true" />
          {t('stageErrors.earlierAttempts', { count: errors.length })}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="mt-2 space-y-2" data-testid="batch-funnel-earlier-attempts-list">
          {errors.map((error, index) => (
            <li
              key={`${error.stage}:${error.code}:${error.runId ?? ''}:${String(index)}`}
              className="bg-background space-y-1 rounded-lg border px-3 py-2 text-sm"
              data-testid={`batch-funnel-earlier-attempt-item-${error.stage}-${error.code}-${String(index)}`}
            >
              <p className="text-foreground flex flex-wrap items-baseline gap-x-2">
                <span className="font-medium">{error.message}</span>
                <StageErrorMeta
                  affectedCount={error.affectedCount}
                  occurrences={error.occurrences}
                  lastOccurredAt={error.lastOccurredAt}
                />
              </p>
              <p className="text-muted-foreground text-xs">
                {KNOWN_ERROR_STAGES.has(error.stage)
                  ? t(`stageErrors.stage.${error.stage}`)
                  : t('stageErrors.otherStage', { stage: error.stage })}
                {' · '}
                <code>{error.code}</code>
              </p>
              <StageErrorDetails
                rootCause={error.rootCause}
                lastOccurredAt={error.lastOccurredAt}
                runId={error.runId}
                testId={`batch-funnel-earlier-attempt-details-${error.stage}-${error.code}-${String(index)}`}
              />
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}
